import type { ChecklistReportResponse, ChecklistReportUploadUrlResponse } from '@/types/settings';

function triggerDownload(blob: Blob, fileName: string) {
  const blobUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = blobUrl;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(blobUrl);
}

function safeFileName(fileName: string) {
  const cleaned = fileName
    .trim()
    .replace(/[^\w.-]+/g, '-')
    .replace(/-+/g, '-');
  return cleaned.endsWith('.pdf') ? cleaned : `${cleaned || 'checklist-report'}.pdf`;
}

async function downloadExistingReport(downloadUrl: string, fileName: string) {
  const response = await fetch(downloadUrl);
  if (!response.ok) throw new Error('Failed to download checklist report');
  triggerDownload(await response.blob(), fileName);
}

export async function downloadChecklistReportPdf(
  appointmentId: string,
  instanceId: string,
  fileName: string,
  timeZone?: string | null,
): Promise<void> {
  const [
    { apiGet, apiPost, operationsClient },
    { settingsEndpoints },
    { queryClient },
    { checklistInstanceKeys },
  ] = await Promise.all([
    import('@/lib/api'),
    import('@/lib/api/endpoints'),
    import('@/lib/queryClient'),
    import('@/lib/queryKeys'),
  ]);

  const report = await apiGet<ChecklistReportResponse>(
    operationsClient,
    settingsEndpoints.appointmentChecklistReport(appointmentId, instanceId),
  );

  const normalizedFileName = safeFileName(fileName);

  if (report.downloadUrl) {
    await downloadExistingReport(report.downloadUrl, normalizedFileName);
    return;
  }

  if (!report.reportData) {
    throw new Error('Checklist report data is not available');
  }

  const [{ ChecklistReportPDFDocument }, { pdf }] = await Promise.all([
    import('@/components/documents/checklists/ChecklistReportPDF'),
    import('@react-pdf/renderer'),
  ]);

  const blob = await pdf(
    <ChecklistReportPDFDocument report={report.reportData} timeZone={timeZone} />,
  ).toBlob();
  triggerDownload(blob, normalizedFileName);

  try {
    const { uploadUrl, fileKey } = await apiPost<ChecklistReportUploadUrlResponse>(
      operationsClient,
      settingsEndpoints.appointmentChecklistReportUploadUrl(appointmentId, instanceId),
    );

    const upload = await fetch(uploadUrl, {
      method: 'PUT',
      body: blob,
      headers: { 'Content-Type': 'application/pdf' },
    });
    if (!upload.ok) throw new Error(`Checklist report upload failed (${upload.status})`);

    await apiPost<unknown, { url: string }>(
      operationsClient,
      settingsEndpoints.appointmentChecklistReportPdf(appointmentId, instanceId),
      { url: fileKey },
    );
    void queryClient.invalidateQueries({
      queryKey: checklistInstanceKeys.byAppointment(appointmentId),
    });
  } catch {
    // Non-fatal: the operator already received the generated PDF.
  }
}
